import React, { useState } from 'react';
import { Modal, Button, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { getAuthorizedMerchants } from '../services/api';
import {
  PROCUREMENT_PREFILL_STATE_KEY,
  ProcurementPrefillState
} from '../types/procurementPrefill';

type PrefillBase = Omit<ProcurementPrefillState, 'channel' | 'merchantId' | 'merchantName'>;

type Props = {
  show: boolean;
  onHide: () => void;
  /** 不含 channel / merchant 的预填内容 */
  prefillBase: PrefillBase | null;
  itemLabel?: string;
};

/**
 * 低库存提醒「去采购」：核对价格提示 → 选内部/商家 → 带预填进入发起申请。
 * 无授权商家时自动走内部审批通道。
 */
export const GoPurchaseFromRestockModal: React.FC<Props> = ({
  show,
  onHide,
  prefillBase,
  itemLabel
}) => {
  const navigate = useNavigate();
  const [step, setStep] = useState<'channel' | 'merchant'>('channel');
  const [loading, setLoading] = useState(false);
  const [merchants, setMerchants] = useState<Array<{ _id: string; name: string }>>([]);
  const [hint, setHint] = useState('');

  const resetLocal = () => {
    setStep('channel');
    setLoading(false);
    setMerchants([]);
    setHint('');
  };

  const handleClose = () => {
    resetLocal();
    onHide();
  };

  const goWithPrefill = (channel: 'internal' | 'merchant', merchantId = '', merchantName = '') => {
    if (!prefillBase) return;
    const state: ProcurementPrefillState = {
      ...prefillBase,
      channel,
      ...(channel === 'merchant'
        ? { merchantId, merchantName }
        : {})
    };
    handleClose();
    navigate('/workbench/application-submission', {
      state: { [PROCUREMENT_PREFILL_STATE_KEY]: state }
    });
  };

  const handleInternal = () => {
    goWithPrefill('internal');
  };

  const handleMerchant = async () => {
    setLoading(true);
    setHint('');
    try {
      const res = await getAuthorizedMerchants();
      const list = res.success && Array.isArray(res.data) ? res.data : [];
      if (list.length === 0) {
        setHint('当前团队暂无已授权商家，将按内部审批发起采购申请。');
        setLoading(false);
        setTimeout(() => goWithPrefill('internal'), 600);
        return;
      }
      setMerchants(list);
      setStep('merchant');
    } catch {
      setHint('获取商家列表失败，请稍后重试或改用内部审批。');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>
          {step === 'channel' ? '去采购' : '选择商家'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {step === 'channel' ? (
          <>
            <p className="mb-2">
              请先核对采购价格、规格与数量
              {itemLabel ? `（${itemLabel}）` : ''}
              ，确认无误后再发起正式申请。申请将走正常审批流程。
            </p>
            <p className="text-muted small mb-3">
              审批通过后，本条低库存提醒将标记为「已处理」；跟进采购到货后自动清除提醒。
            </p>
            {hint ? <div className="alert alert-info py-2 small mb-3">{hint}</div> : null}
            <div className="d-grid gap-2">
              <Button variant="primary" onClick={handleInternal} disabled={loading}>
                内部审批采购
              </Button>
              <Button variant="outline-primary" onClick={handleMerchant} disabled={loading}>
                {loading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    加载商家…
                  </>
                ) : (
                  '商家通道采购'
                )}
              </Button>
            </div>
          </>
        ) : (
          <>
            <p className="text-muted small mb-3">请选择商家后进入选购/申请流程（表单将预填当前补货信息）。</p>
            <div className="d-grid gap-2">
              {merchants.map((m) => (
                <Button
                  key={m._id}
                  variant="outline-secondary"
                  className="text-start"
                  onClick={() => goWithPrefill('merchant', m._id, m.name)}
                >
                  {m.name}
                </Button>
              ))}
            </div>
            <Button variant="link" className="mt-2 px-0" onClick={() => setStep('channel')}>
              返回
            </Button>
          </>
        )}
      </Modal.Body>
    </Modal>
  );
};
